(() => {
    "use strict";

    const pageGap = 8;
    const pageInset = 24;

    const chipsOf = (root) => Array.from(root.querySelectorAll(".temas-palestrantes-chip"));

    const chipWidth = (chip) => Math.ceil(Math.max(
        chip.getBoundingClientRect().width,
        chip.offsetWidth,
        chip.scrollWidth
    ));

    const packPages = (available, chips) => {
        const pages = [];
        let current = [];
        let used = 0;

        for (let index = 0; index < chips.length; index += 1) {
            const width = chipWidth(chips[index]);
            if (width < 8) {
                return [];
            }
            const next = current.length === 0 ? width : used + pageGap + width;
            if (current.length > 0 && next > available) {
                pages.push(current);
                current = [chips[index]];
                used = width;
                continue;
            }
            current.push(chips[index]);
            used = next;
        }

        if (current.length) {
            pages.push(current);
        }

        return pages;
    };

    const flatten = (root, source) => {
        chipsOf(root).forEach((chip) => source.appendChild(chip));
        root.querySelectorAll("[data-temas-page]").forEach((page) => page.remove());
    };

    const paginate = (root) => {
        const viewport = root.querySelector("[data-temas-viewport]");
        const track = root.querySelector("[data-temas-track]");
        const source = root.querySelector("[data-temas-source]");
        const dotsHost = root.querySelector("[data-temas-dots]");
        if (!viewport || !track || !source || !dotsHost) {
            return 0;
        }

        flatten(root, source);
        source.hidden = false;
        const chips = chipsOf(source);
        if (!chips.length) {
            dotsHost.hidden = true;
            dotsHost.replaceChildren();
            root.classList.add("is-ready");
            return 0;
        }

        const packed = packPages(Math.max(viewport.clientWidth - pageInset, 1), chips);
        if (!packed.length) {
            return -1;
        }

        const pageCount = packed.length;
        const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

        if (pageCount < 2) {
            dotsHost.hidden = true;
            dotsHost.replaceChildren();
            source.hidden = false;
            track.style.transform = "";
            root.classList.add("is-ready");
            return 1;
        }

        source.hidden = true;
        packed.forEach((pageChips, pageIndex) => {
            const page = document.createElement("div");
            page.className = "temas-slider-page";
            page.setAttribute("data-temas-page", String(pageIndex));
            page.setAttribute("aria-hidden", pageIndex === 0 ? "false" : "true");
            if (pageIndex !== 0) {
                page.setAttribute("inert", "");
            }
            pageChips.forEach((chip) => page.appendChild(chip));
            track.appendChild(page);
        });

        dotsHost.hidden = false;
        dotsHost.replaceChildren();
        for (let pageIndex = 0; pageIndex < pageCount; pageIndex += 1) {
            const dot = document.createElement("button");
            dot.type = "button";
            dot.className = "temas-slider-dot" + (pageIndex === 0 ? " is-active" : "");
            dot.setAttribute("data-temas-dot", String(pageIndex));
            dot.setAttribute("role", "tab");
            dot.setAttribute("aria-selected", pageIndex === 0 ? "true" : "false");
            dot.setAttribute("aria-label", "Ir para o grupo de temas " + (pageIndex + 1));
            dot.tabIndex = pageIndex === 0 ? 0 : -1;
            dotsHost.appendChild(dot);
        }

        if (reducedMotion) {
            track.style.transition = "none";
        }

        root.classList.add("is-ready");
        return pageCount;
    };

    const activate = (root, requestedIndex, pageCount, focusDot = false) => {
        const track = root.querySelector("[data-temas-track]");
        const viewport = root.querySelector("[data-temas-viewport]");
        const pages = Array.from(root.querySelectorAll("[data-temas-page]"));
        const dots = Array.from(root.querySelectorAll("[data-temas-dot]"));
        if (!track || pageCount < 2 || !pages.length) {
            return 0;
        }

        const activeIndex = ((requestedIndex % pageCount) + pageCount) % pageCount;
        const offset = viewport ? viewport.clientWidth * activeIndex : 0;
        track.style.transform = "translate3d(-" + offset + "px, 0, 0)";

        pages.forEach((page, index) => {
            const isActive = index === activeIndex;
            page.classList.toggle("is-active", isActive);
            page.setAttribute("aria-hidden", String(!isActive));
            page.toggleAttribute("inert", !isActive);
        });

        dots.forEach((dot, index) => {
            const isActive = index === activeIndex;
            dot.classList.toggle("is-active", isActive);
            dot.setAttribute("aria-selected", String(isActive));
            dot.tabIndex = isActive ? 0 : -1;
        });

        if (focusDot) {
            dots[activeIndex]?.focus();
        }

        root.dataset.temasPage = String(activeIndex);
        return activeIndex;
    };

    const bind = (root) => {
        let pageCount = 0;
        let activeIndex = 0;
        let tries = 0;

        const layout = () => {
            const nextCount = paginate(root);
            if (nextCount < 0 && tries < 12) {
                tries += 1;
                window.setTimeout(layout, 50);
                return;
            }
            pageCount = Math.max(nextCount, 0);
            activeIndex = activate(root, Number(root.dataset.temasPage || 0), pageCount);
        };

        const go = (index, focusDot = false) => {
            if (pageCount < 2) {
                return;
            }
            activeIndex = activate(root, index, pageCount, focusDot);
        };

        root.addEventListener("click", (event) => {
            const dot = event.target.closest("[data-temas-dot]");
            if (!dot || !root.contains(dot)) {
                return;
            }
            go(Number(dot.getAttribute("data-temas-dot") || 0));
        });

        root.addEventListener("keydown", (event) => {
            if (event.target.closest("a, input, select, textarea")) {
                return;
            }
            if (event.key === "ArrowLeft") {
                event.preventDefault();
                go(activeIndex - 1, true);
            }
            if (event.key === "ArrowRight") {
                event.preventDefault();
                go(activeIndex + 1, true);
            }
        });

        const viewport = root.querySelector("[data-temas-viewport]");
        let touchStartX = 0;
        viewport?.addEventListener("pointerdown", (event) => {
            touchStartX = event.clientX;
        }, { passive: true });
        viewport?.addEventListener("pointerup", (event) => {
            const delta = event.clientX - touchStartX;
            if (Math.abs(delta) < 40) {
                return;
            }
            go(activeIndex + (delta < 0 ? 1 : -1));
        });

        let resizeTimer = 0;
        window.addEventListener("resize", () => {
            window.clearTimeout(resizeTimer);
            resizeTimer = window.setTimeout(() => {
                tries = 0;
                layout();
            }, 160);
        });

        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(layout);
        });
    };

    function boot() {
        document.querySelectorAll("[data-temas-slider]").forEach((root) => {
            if (root.dataset.bound === "true") {
                return;
            }
            root.dataset.bound = "true";
            bind(root);
        });
    }

    window.siteReady(boot);
})();
